import { NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { LayoutService } from '@/layout/service/layout.service';
import { NavegacaoStore } from '@/layout/store/navegacao.store';
import { AuthService } from '@services/auth.service';
import { Usuario } from '@interfaces/usuario';
import { GravatarModule } from 'ngx-gravatar';
import { ButtonModule } from 'primeng/button';
import { StyleClassModule } from 'primeng/styleclass';
import { TooltipModule } from 'primeng/tooltip';
import { AppBreadcrumb } from './app.breadcrumb';
import { TipoTema } from '@/shared/enums/tipo-tema.enum';
import { BuscaGlobalComponent } from './busca-global/busca-global.component';

@Component({
  selector: '[app-topbar]',
  imports: [
    StyleClassModule,
    AppBreadcrumb,
    BuscaGlobalComponent,
    ButtonModule,
    GravatarModule,
    NgOptimizedImage,
    TooltipModule,
  ],
  templateUrl: './app.topbar.html',
  styleUrl: './app.topbar.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppTopbar {
  @ViewChild('menubutton') public menuButton!: ElementRef<HTMLButtonElement>;

  protected readonly layoutService = inject(LayoutService);
  private readonly navegacaoStore = inject(NavegacaoStore);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  private readonly usuario = signal<Usuario | undefined>(undefined);

  protected readonly emailGravatar = computed(() => {
    const email = this.usuario()?.email?.trim();
    if (email !== undefined && email !== '' && email.includes('@')) {
      return email;
    }
    return undefined;
  });

  protected readonly loginExibicao = computed(() => {
    const u = this.usuario();
    if (u === undefined) {
      return '—';
    }
    const login = u.sub?.trim();
    return login !== undefined && login !== '' ? login : '—';
  });

  protected readonly siglaUnidade = computed(() => {
    const sigla = this.usuario()?.unidadeSigla?.trim();
    return sigla !== undefined && sigla !== '' ? sigla : '—';
  });

  protected readonly rotuloBlocoUsuario = computed(
    () => `Usuário ${this.loginExibicao()}, unidade ${this.siglaUnidade()}`,
  );

  protected readonly iconeTema = computed(() =>
    this.layoutService.isDarkTheme() ? 'pi pi-sun' : 'pi pi-moon',
  );

  protected readonly rotuloAlternarTema = computed(() =>
    this.layoutService.isDarkTheme() ? 'Ativar modo claro' : 'Ativar modo escuro',
  );

  protected readonly modoEscuroAtivo = computed(
    () => this.layoutService.layoutConfig().darkTheme === true,
  );

  protected readonly alterandoTema = signal(false);

  constructor() {
    try {
      const bruto = localStorage.getItem('usuario');
      if (bruto !== null) {
        this.usuario.set(JSON.parse(bruto) as Usuario);
        this.layoutService.definirTemaPreferencial(this.usuario()?.tema);
      }
    } catch {
      this.usuario.set(undefined);
    }
  }

  public onMenuButtonClick(): void {
    if (this.layoutService.isMobile() && this.navegacaoStore.emProntuario()) {
      this.layoutService.layoutState.update((prev) => {
        const aberto = prev.menuNavegacaoDrawerAtivo !== true;
        return {
          ...prev,
          menuNavegacaoDrawerAtivo: aberto,
          staticMenuMobileActive: false,
        };
      });
      if (this.layoutService.layoutState().menuNavegacaoDrawerAtivo === true) {
        this.layoutService.notificarMenuMobileOuDrawerAberto();
      } else {
        document.body.classList.remove('blocked-scroll');
      }
      return;
    }
    this.layoutService.onMenuToggle();
  }

  public alternarTemaClaroEscuro(): void {
    this.alterandoTema.set(true);
    this.authService.atualizaPreferenciaTema(
      this.modoEscuroAtivo() ? TipoTema.Claro : TipoTema.Escuro
    ).subscribe({
      next: () => {
        this.layoutService.alternarModoClaroEscuro();
      },
      error: (err) => {
        console.log('Erro ao atualizar tema:', err.message);
      },
      complete: () => {
        this.alterandoTema.set(false);
      },
    });
  }

  public encerrarSessao(): void {
    this.authService.logout();
    void this.router.navigate(['/login']);
  }
}
